import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, Plus, Trash2, Edit2, X, Repeat, CalendarDays } from 'lucide-react';

const CalendrierPlagesHoraires = ({ plages = [], onChange, readOnly = false }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlage, setEditingPlage] = useState(null);
  
  // Gestion swipe mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const joursNoms = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const joursAbbr = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
  const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      if (view === 'month') navigateMonth(1);
      else if (view === 'week') navigateWeek(1);
      else navigateDay(1);
    }
    if (isRightSwipe) {
      if (view === 'month') navigateMonth(-1);
      else if (view === 'week') navigateWeek(-1);
      else navigateDay(-1);
    }
  };

  // Vérifier si une date a des plages
  const getPlagesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dayOfWeek = date.getDay();
    
    return (plages || []).filter(p => {
      // Plage unique (date spécifique)
      if (p.typeRecurrence === 'unique') {
        const plageDate = new Date(p.dateSpecifique).toISOString().split('T')[0];
        return plageDate === dateStr;
      }
      
      // Plage hebdomadaire (tous les lundis, mardis...)
      if (p.typeRecurrence === 'hebdomadaire') {
        return p.jourSemaine === dayOfWeek;
      }
      
      // Plage quotidienne (période)
      if (p.typeRecurrence === 'quotidien') {
        const debut = new Date(p.dateDebut);
        const fin = new Date(p.dateFin);
        return date >= debut && date <= fin;
      }
      
      return false;
    });
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const handleSavePlage = (plageData) => {
    const nouvelles = [...(plages || [])];
    
    if (editingPlage !== null) {
      nouvelles[editingPlage] = plageData;
    } else {
      nouvelles.push(plageData);
    }

    onChange(nouvelles);
    setShowModal(false);
    setEditingPlage(null);
  };

  const handleDeletePlage = (index) => {
    const nouvelles = plages.filter((_, i) => i !== index);
    onChange(nouvelles);
    setShowModal(false);
  };

  // Rendu vue mois
  const renderMonthView = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const plagesDuJour = getPlagesForDate(date);
      const hasPlages = plagesDuJour.length > 0;

      days.push(
        <button
          key={day}
          onClick={() => {
            if (!readOnly) {
              setSelectedDate(date);
              setShowModal(true);
            }
          }}
          className={`aspect-square p-1 rounded-lg border-2 transition-all active:scale-95 ${
            hasPlages 
              ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600 shadow-md' 
              : 'bg-white border-gray-300 hover:border-blue-400'
          } ${!readOnly ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <div className={`text-xs font-bold ${hasPlages ? 'text-white' : 'text-gray-700'}`}>
            {day}
          </div>
          {hasPlages && (
            <div className="mt-0.5">
              <div className="w-1 h-1 bg-white rounded-full mx-auto"></div>
            </div>
          )}
        </button>
      );
    }

    return (
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {joursAbbr.map(jour => (
          <div key={jour} className="text-center text-[10px] sm:text-xs font-bold text-gray-600 pb-1 sm:pb-2">
            {jour}
          </div>
        ))}
        {days}
      </div>
    );
  };

  // Rendu vue semaine
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    return (
      <div className="space-y-2">
        {[0, 1, 2, 3, 4, 5, 6].map(offset => {
          const date = new Date(startOfWeek);
          date.setDate(startOfWeek.getDate() + offset);
          const plagesDuJour = getPlagesForDate(date);

          return (
            <button
              key={offset}
              onClick={() => {
                if (!readOnly) {
                  setSelectedDate(date);
                  setShowModal(true);
                }
              }}
              className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left active:scale-[0.98] ${
                plagesDuJour.length > 0
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                  : 'bg-white border-gray-200 hover:border-blue-300'
              } ${!readOnly ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-bold text-gray-800 text-sm sm:text-base">
                    {joursNoms[date.getDay()]}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {date.getDate()} {moisNoms[date.getMonth()].substring(0, 3)}
                  </div>
                </div>
                {plagesDuJour.length > 0 && (
                  <div className="bg-green-500 text-white px-2 py-1 rounded-full text-[10px] sm:text-xs font-bold">
                    {plagesDuJour.length}
                  </div>
                )}
              </div>
              {plagesDuJour.length > 0 && (
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  {plagesDuJour.slice(0, 2).map((plage, idx) => (
                    <div key={idx} className="bg-green-100 border border-green-300 text-green-800 px-2 py-0.5 sm:px-3 sm:py-1 rounded-lg text-[10px] sm:text-sm font-bold">
                      {plage.heureDebut}-{plage.heureFin}
                    </div>
                  ))}
                  {plagesDuJour.length > 2 && (
                    <div className="text-[10px] text-gray-500">+{plagesDuJour.length - 2}</div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // Rendu vue jour
  const renderDayView = () => {
    const plagesDuJour = getPlagesForDate(currentDate);

    return (
      <div className="space-y-3 sm:space-y-4">
        <div className="text-center mb-4 sm:mb-6">
          <div className="text-xl sm:text-2xl font-bold text-gray-800">
            {joursNoms[currentDate.getDay()]}
          </div>
          <div className="text-sm sm:text-base text-gray-500">
            {currentDate.getDate()} {moisNoms[currentDate.getMonth()]} {currentDate.getFullYear()}
          </div>
        </div>

        {plagesDuJour.length === 0 ? (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-8 sm:p-10 text-center">
            <Clock size={40} className="mx-auto mb-3 text-gray-300 sm:w-12 sm:h-12" />
            <p className="text-sm sm:text-base text-gray-500 font-medium mb-3 sm:mb-4">
              Aucune plage horaire
            </p>
            {!readOnly && (
              <button
                onClick={() => {
                  setSelectedDate(currentDate);
                  setShowModal(true);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl transition-all active:scale-95 text-sm sm:text-base"
              >
                + Ajouter
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {plagesDuJour.map((plage, idx) => {
              const globalIndex = plages.findIndex(p => p === plage);
              
              // Déterminer le type d'affichage
              let typeLabel = '';
              let typeIcon = <Repeat size={16} />;
              
              if (plage.typeRecurrence === 'unique') {
                typeLabel = 'Une fois';
                typeIcon = <Calendar size={16} />;
              } else if (plage.typeRecurrence === 'hebdomadaire') {
                typeLabel = `Tous les ${joursNoms[plage.jourSemaine].toLowerCase()}s`;
                typeIcon = <Repeat size={16} />;
              } else if (plage.typeRecurrence === 'quotidien') {
                typeLabel = 'Tous les jours (1 mois)';
                typeIcon = <CalendarDays size={16} />;
              }
              
              return (
                <div key={idx} className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <div className="bg-green-500 rounded-full p-1.5 sm:p-2 flex-shrink-0">
                        <Clock size={16} className="text-white sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-gray-800 text-base sm:text-lg">
                          {plage.heureDebut} - {plage.heureFin}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                          {typeIcon}
                          <span className="truncate">{typeLabel}</span>
                        </div>
                      </div>
                    </div>
                    {!readOnly && (
                      <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                        <button
                          onClick={() => {
                            setSelectedDate(currentDate);
                            setEditingPlage(globalIndex);
                            setShowModal(true);
                          }}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-all active:scale-95"
                        >
                          <Edit2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </button>
                        <button
                          onClick={() => handleDeletePlage(globalIndex)}
                          className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-all active:scale-95"
                        >
                          <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {!readOnly && (
              <button
                onClick={() => {
                  setSelectedDate(currentDate);
                  setShowModal(true);
                }}
                className="w-full bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 text-blue-700 font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 text-sm sm:text-base"
              >
                <Plus size={18} className="sm:w-5 sm:h-5" />
                Ajouter une plage
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => {
              if (view === 'month') navigateMonth(-1);
              else if (view === 'week') navigateWeek(-1);
              else navigateDay(-1);
            }}
            className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-all active:scale-95 flex-shrink-0"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="text-center min-w-[140px] sm:min-w-[180px]">
            <div className="font-bold text-gray-800 text-sm sm:text-base">
              {view === 'day' 
                ? `${currentDate.getDate()} ${moisNoms[currentDate.getMonth()]}`
                : moisNoms[currentDate.getMonth()]
              }
            </div>
            <div className="text-xs sm:text-sm text-gray-500">{currentDate.getFullYear()}</div>
          </div>
          
          <button
            onClick={() => {
              if (view === 'month') navigateMonth(1);
              else if (view === 'week') navigateWeek(1);
              else navigateDay(1);
            }}
            className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-all active:scale-95 flex-shrink-0"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
          <button
            onClick={() => setView('month')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${
              view === 'month' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            Mois
          </button>
          <button
            onClick={() => setView('week')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${
              view === 'week' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            Semaine
          </button>
          <button
            onClick={() => setView('day')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${
              view === 'day' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            Jour
          </button>
        </div>
      </div>

      <div className="text-center text-xs text-gray-400 sm:hidden">
        👈 Glissez pour naviguer 👉
      </div>

      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="touch-pan-y"
      >
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
      </div>

      {showModal && (
        <ModalPlageRecurrence
          dateSelectionnee={selectedDate}
          plagesExistantes={getPlagesForDate(selectedDate)}
          allPlages={plages}
          onSave={handleSavePlage}
          onDelete={handleDeletePlage}
          onClose={() => {
            setShowModal(false);
            setEditingPlage(null);
          }}
          editingIndex={editingPlage}
          plageEditee={editingPlage !== null ? plages[editingPlage] : null}
        />
      )}
    </div>
  );
};
// Modal avec sélection de récurrence ET gestion plages existantes
const ModalPlageRecurrence = ({ dateSelectionnee, plagesExistantes, onSave, onDelete, onClose, editingIndex, plageEditee, allPlages }) => {
  const joursNoms = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  const [mode, setMode] = useState(editingIndex !== null ? 'edit' : 'list'); // 'list', 'add', 'edit'
  const [typeRecurrence, setTypeRecurrence] = useState(plageEditee?.typeRecurrence || 'hebdomadaire');
  const [heureDebut, setHeureDebut] = useState(plageEditee?.heureDebut || '09:00');
  const [heureFin, setHeureFin] = useState(plageEditee?.heureFin || '12:00');
  const [selectedPlageIndex, setSelectedPlageIndex] = useState(editingIndex);

  const jourSemaine = dateSelectionnee.getDay();
  const dateStr = dateSelectionnee.toLocaleDateString('fr-FR');

  const handleEdit = (plage, index) => {
    setSelectedPlageIndex(index);
    setTypeRecurrence(plage.typeRecurrence || 'hebdomadaire');
    setHeureDebut(plage.heureDebut);
    setHeureFin(plage.heureFin);
    setMode('edit');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const plageData = {
      typeRecurrence,
      heureDebut,
      heureFin
    };

    if (typeRecurrence === 'unique') {
      plageData.dateSpecifique = dateSelectionnee.toISOString();
    } else if (typeRecurrence === 'hebdomadaire') {
      plageData.jourSemaine = jourSemaine;
    } else if (typeRecurrence === 'quotidien') {
      const debut = new Date(dateSelectionnee);
      const fin = new Date(dateSelectionnee.getFullYear(), dateSelectionnee.getMonth() + 1, 0);
      
      plageData.dateDebut = debut.toISOString();
      plageData.dateFin = fin.toISOString();
    }

    onSave(plageData);
  };

  // Mode Liste - Afficher plages existantes
  if (mode === 'list' && plagesExistantes.length > 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 sm:p-6 rounded-t-3xl sm:rounded-t-2xl sticky top-0">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-bold">
                  Plages horaires
                </h3>
                <p className="text-xs sm:text-sm text-green-100 mt-1">
                  {joursNoms[jourSemaine]} {dateStr}
                </p>
              </div>
              <button
                onClick={onClose}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-all active:scale-95"
              >
                <X size={20} className="text-white" />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-3">
            {/* Liste des plages */}
            {plagesExistantes.map((plage, idx) => {
              const globalIndex = allPlages.findIndex(p => p === plage);
              
              let typeLabel = '';
              let typeIcon = <Repeat size={16} />;
              
              if (plage.typeRecurrence === 'unique') {
                typeLabel = 'Une fois';
                typeIcon = <Calendar size={16} />;
              } else if (plage.typeRecurrence === 'hebdomadaire') {
                typeLabel = `Tous les ${joursNoms[plage.jourSemaine].toLowerCase()}s`;
                typeIcon = <Repeat size={16} />;
              } else if (plage.typeRecurrence === 'quotidien') {
                typeLabel = 'Tous les jours (1 mois)';
                typeIcon = <CalendarDays size={16} />;
              }
              
              return (
                <div key={idx} className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <div className="bg-green-500 rounded-full p-1.5 sm:p-2 flex-shrink-0">
                        <Clock size={16} className="text-white sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-gray-800 text-base sm:text-lg">
                          {plage.heureDebut} - {plage.heureFin}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                          {typeIcon}
                          <span className="truncate">{typeLabel}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                      <button
                        onClick={() => handleEdit(plage, globalIndex)}
                        className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-all active:scale-95"
                      >
                        <Edit2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </button>
                      <button
                        onClick={() => onDelete(globalIndex)}
                        className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-all active:scale-95"
                      >
                        <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Bouton ajouter */}
            <button
              onClick={() => setMode('add')}
              className="w-full bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 text-blue-700 font-bold py-3 sm:py-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <Plus size={20} />
              Ajouter une plage
            </button>

            <div className="h-4 sm:hidden"></div>
          </div>
        </div>
      </div>
    );
  }

  // Mode Add/Edit - Formulaire
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-t-3xl sm:rounded-t-2xl sticky top-0">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-lg sm:text-xl font-bold">
                {mode === 'edit' ? 'Modifier' : 'Ajouter'} plage
              </h3>
              <p className="text-xs sm:text-sm text-blue-100 mt-1">
                {joursNoms[jourSemaine]} {dateStr}
              </p>
            </div>
            <button
              onClick={() => {
                if (plagesExistantes.length > 0 && mode !== 'edit') {
                  setMode('list');
                } else {
                  onClose();
                }
              }}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-all active:scale-95"
            >
              <X size={20} className="text-white" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Type de récurrence */}
          <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-3">
              🔁 Récurrence (max 1 mois)
            </label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setTypeRecurrence('unique')}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                  typeRecurrence === 'unique'
                    ? 'bg-blue-50 border-blue-500 shadow-md'
                    : 'bg-white border-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Calendar size={18} className={typeRecurrence === 'unique' ? 'text-blue-600' : 'text-gray-500'} />
                  <div>
                    <div className={`font-bold text-sm ${typeRecurrence === 'unique' ? 'text-blue-700' : 'text-gray-700'}`}>
                      Une seule fois
                    </div>
                    <div className="text-xs text-gray-500">Ce jour uniquement : {dateStr}</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setTypeRecurrence('hebdomadaire')}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                  typeRecurrence === 'hebdomadaire'
                    ? 'bg-blue-50 border-blue-500 shadow-md'
                    : 'bg-white border-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Repeat size={18} className={typeRecurrence === 'hebdomadaire' ? 'text-blue-600' : 'text-gray-500'} />
                  <div>
                    <div className={`font-bold text-sm ${typeRecurrence === 'hebdomadaire' ? 'text-blue-700' : 'text-gray-700'}`}>
                      Toutes les semaines
                    </div>
                    <div className="text-xs text-gray-500">Tous les {joursNoms[jourSemaine].toLowerCase()}s</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setTypeRecurrence('quotidien')}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                  typeRecurrence === 'quotidien'
                    ? 'bg-blue-50 border-blue-500 shadow-md'
                    : 'bg-white border-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CalendarDays size={18} className={typeRecurrence === 'quotidien' ? 'text-blue-600' : 'text-gray-500'} />
                  <div>
                    <div className={`font-bold text-sm ${typeRecurrence === 'quotidien' ? 'text-blue-700' : 'text-gray-700'}`}>
                      Tous les jours du mois
                    </div>
                    <div className="text-xs text-gray-500">
                      Du {dateSelectionnee.getDate()} {moisNoms[dateSelectionnee.getMonth()]} au dernier jour
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Horaires */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Début</label>
              <input
                type="time"
                value={heureDebut}
                onChange={(e) => setHeureDebut(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-bold text-sm sm:text-base"
                required
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Fin</label>
              <input
                type="time"
                value={heureFin}
                onChange={(e) => setHeureFin(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-bold text-sm sm:text-base"
                required
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 sm:gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                if (plagesExistantes.length > 0 && mode !== 'edit') {
                  setMode('list');
                } else {
                  onClose();
                }
              }}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 sm:py-3.5 rounded-xl transition-all active:scale-95 text-sm sm:text-base"
            >
              {plagesExistantes.length > 0 && mode !== 'edit' ? 'Retour' : 'Annuler'}
            </button>
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 sm:py-3.5 rounded-xl transition-all active:scale-95 shadow-lg text-sm sm:text-base"
            >
              {mode === 'edit' ? 'Modifier' : 'Ajouter'}
            </button>
          </div>

          <div className="h-4 sm:hidden"></div>
        </form>
      </div>
    </div>
  );
};

export default CalendrierPlagesHoraires;
